import React, { useState, useEffect, useCallback } from 'react';
import { MultiSelect } from '@mantine/core';

/**
 * GuideSelect - Componente de selección optimizado para el Generador de Guías
 * 
 * Versión simplificada de OSelect sin ciclos infinitos de re-render.
 * Usa un flujo de datos unidireccional:
 * Formik value → Visualización → User interaction → onChange → Formik update
 */
const GuideSelect = ({
	label,
	labelExtra = '',
	name,
	value,
	onSelect,
	handleLabelOptions,
	disabled,
	requestApi,
	itemsSelect = [],
	isMulti = false,
	maxSelectedValues,
	validation,
	isRequired,
	value_key = '_id',
}) => {
	const [isLoading, setIsLoading] = useState(requestApi != null);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [listItemsSelect, setListItemsSelect] = useState([]);
	const [hasFetched, setHasFetched] = useState(false);

	// Cargar datos desde API solo una vez
	useEffect(() => {
		if (requestApi != null && !hasFetched) {
			setHasFetched(true);
			setIsLoading(true);
			requestApi()
				.then(response => {
					const items = response.map(item => ({
						item: item,
						value: item[value_key],
						code: item.code,
						label: handleLabelOptions ? handleLabelOptions(item) : item.name,
					}));
					setListItemsSelect(items);
					setIsLoading(false);
				})
				.catch(err => {
					console.log(err.message);
					setIsLoading(false);
				});
		}
	}, [requestApi, hasFetched, value_key, handleLabelOptions]);

	// Usar itemsSelect directamente para opciones locales
	useEffect(() => {
		if (requestApi == null) {
			if (Array.isArray(itemsSelect) && itemsSelect.length > 0) {
				const items = itemsSelect.map(item => ({
					...item,
					label: item.label || (handleLabelOptions ? handleLabelOptions(item.item) : item.item?.name),
				}));
				setListItemsSelect(items);
			} else {
				setListItemsSelect([]);
			}
			setIsLoading(false);
		}
	}, [itemsSelect, requestApi, handleLabelOptions]);

	// Manejar cambio de selección
	const handleChange = useCallback((newSelectedValues) => {
		// Convertir a valor único si no es multi
		const finalValue = isMulti 
			? newSelectedValues 
			: (newSelectedValues?.[0] || null);

		// Actualizar formulario solo si el valor realmente cambió
		const currentValue = validation.values[name];
		if (currentValue !== finalValue) {
			validation.setFieldValue(name, finalValue);
			validation.setFieldTouched(name, true);
		}

		// Llamar onSelect si existe
		if (onSelect && finalValue != null) {
			const selectedItem = listItemsSelect.find(item => item.value == finalValue);
			if (selectedItem) {
				onSelect(isMulti ? [selectedItem.item] : selectedItem.item);
			}
		}
	}, [isMulti, name, validation, onSelect, listItemsSelect]);

	// Preparar valor para MultiSelect (debe ser array)
	const multiSelectValue = isMulti 
		? (Array.isArray(value) ? value : [value].filter(Boolean))
		: [value].filter(Boolean);

	return (
		<React.Fragment>
			<label htmlFor={`${name}`} className='form-label'>
				{label} {isRequired && <span style={{ color: 'red' }}>*</span>}
				{labelExtra ? ` ${labelExtra}` : ''}
			</label>
			<MultiSelect
				id={`${name}`}
				name={`${name}`}
				clearable={true}
				value={multiSelectValue}
				data={listItemsSelect}
				onChange={handleChange}
				onBlur={validation.handleBlur}
				maxSelectedValues={isMulti ? maxSelectedValues : 1}
				searchable
				placeholder={isLoading ? 'Cargando...' : `Seleccione ${label}`}
				disabled={isLoading || disabled}
				error={
					validation.touched[name] && validation.errors[name]
						? validation.errors[name]
						: ''
				}
				{...(isMulti && { multiple: true })}
				onDropdownClose={() => setIsDropdownOpen(false)}
				onDropdownOpen={() => setIsDropdownOpen(true)}
			/>
		</React.Fragment>
	);
};

export default GuideSelect;
